'use client';

import { useCallback, useState } from 'react';

function focusedElement(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const active = document.activeElement;
  return active instanceof HTMLElement && active !== document.body
    ? active
    : null;
}

/**
 * Focus return for controlled Radix dialogs opened from outside their own
 * `DialogTrigger` (a menu item, a provider call, a button that only flips
 * `open`). Radix restores focus to its trigger on close; without one, focus
 * falls to the document body. The opener is captured in the render where
 * `open` turns true, before the dialog mounts and moves focus (Radix skips
 * `onOpenAutoFocus` when a field inside autofocuses, so it can't be the
 * capture point). `onCloseAutoFocus` hands focus back to the opener if it is
 * still in the document.
 *
 * Spread the result onto `DialogContent` / `AlertDialogContent` (or a vaul
 * `DrawerContent`). `onOpen` keeps a caller's own open-focus handler.
 */
export function useDialogFocusReturn(
  open: boolean,
  onOpen?: (event: Event) => void
) {
  const [opener, setOpener] = useState<HTMLElement | null>(() =>
    open ? focusedElement() : null
  );
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setOpener(focusedElement());
  }
  const onOpenAutoFocus = useCallback(
    (event: Event) => onOpen?.(event),
    [onOpen]
  );
  const onCloseAutoFocus = useCallback(
    (event: Event) => {
      if (!opener?.isConnected) return;
      event.preventDefault();
      opener.focus({ preventScroll: true });
    },
    [opener]
  );
  return { onOpenAutoFocus, onCloseAutoFocus };
}
